import { useCallback, useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { showError, showInfo } from '../../ui/appToast';
import {
  isUnexpectedFailureHandled,
  presentUnexpectedFailure,
} from '../../sentry/presentUnexpectedFailure';

import type { CatalogCsvEntityKind } from '../../csv-core/public-api';
import type { CsvFlowAdapter, CsvPreviewResult, CsvPreviewRow } from './csvFlowAdapter';
import { pickCsvFile } from '../../csv-core/infra/pickCsvFile'
import { shareOrDownloadCsv } from '../../csv-core/infra/shareCsv'
import { getCsvColumnHints, getCsvGuideNote, getCsvHeroSubtitle } from '../../csv-core/domain/csvUiCopy'
import type { CsvFlowMode, CsvImportDoneCounts } from '../../csv-core/domain/csvUiCopy';
import {
  previewRowHasAviso,
  sortPreviewRowsForReview,
  type PreviewPageSize,
  type PreviewRowView,
  type PreviewCounts,
} from './csvPreviewHelpers';

export type { CsvFlowMode };
export type { CsvFlowAdapter } from './csvFlowAdapter';

export type CsvImportExportStep = 'guide' | 'preview' | 'done';

const CATALOG_ENTITY_LABELS: Record<CatalogCsvEntityKind, string> = {
  categories: 'Categorías',
  brands: 'Marcas',
  suppliers: 'Proveedores',
  products: 'Productos',
};

type UseCsvImportExportFlowParams = {
  mode: CsvFlowMode;
  catalogEntity?: CatalogCsvEntityKind;
  branchId?: string | null;
  branchLabel?: string;
  maxProducts?: number | null;
  performedByUserId?: string | null;
  onApplied?: () => void | Promise<void>;
  adapter: CsvFlowAdapter;
};

export function useCsvImportExportFlow({
  mode,
  catalogEntity = 'products',
  branchId = null,
  branchLabel,
  maxProducts = null,
  performedByUserId = null,
  onApplied,
  adapter,
}: UseCsvImportExportFlowParams) {
  const [step, setStep] = useState<CsvImportExportStep>('guide');
  const [fileName, setFileName] = useState<string | null>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [preview, setPreview] = useState<CsvPreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [lastDoneCounts, setLastDoneCounts] = useState<CsvImportDoneCounts | null>(null);
  const [lastErrors, setLastErrors] = useState<Array<{ rowNumber: number; message: string }>>([]);

  const countOkBreakdown = useCallback((rows: CsvPreviewRow[] | undefined) => {
    let altas = 0;
    let ediciones = 0;
    for (const row of rows ?? []) {
      if (row.status !== 'ok') continue;
      if (row.action === 'crear') altas += 1;
      else ediciones += 1;
    }
    return { altas, ediciones };
  }, []);

  const entityLabel =
    mode === 'stock' ? 'Stock' : mode === 'customers' ? 'Clientes' : CATALOG_ENTITY_LABELS[catalogEntity ?? 'products'];

  const reset = useCallback(() => {
    setStep('guide');
    setFileName(null);
    setCsvText(null);
    setPreview(null);
    setLastDoneCounts(null);
    setLastErrors([]);
  }, []);

  const downloadTemplate = useCallback(async () => {
    try {
      setLoading(true);
      const csv = adapter.buildTemplate();
      const filename = adapter.templateFilename();
      await shareOrDownloadCsv(csv, filename);
    } catch (e) {
      const userMessage = e instanceof Error ? e.message : 'No se pudo generar la plantilla.';
      if (isUnexpectedFailureHandled(e)) {
        // shareOrDownloadCsv ya reportó a Sentry.
        showError('Plantilla', userMessage);
        return;
      }
      presentUnexpectedFailure({
        module: 'csv',
        op: 'template',
        error: e,
        title: 'Plantilla',
        userMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [adapter]);

  const downloadExport = useCallback(async () => {
    try {
      setLoading(true);
      if (mode === 'stock' && !branchId) {
        showInfo('Exportar', 'Seleccioná una sucursal.');
        return;
      }
      const csv = await adapter.buildExport();
      await shareOrDownloadCsv(csv, adapter.exportFilename());
    } catch (e) {
      const userMessage = e instanceof Error ? e.message : 'No se pudo exportar.';
      if (isUnexpectedFailureHandled(e)) {
        // shareOrDownloadCsv ya reportó a Sentry.
        showError('Exportar', userMessage);
        return;
      }
      presentUnexpectedFailure({
        module: 'csv',
        op: 'export',
        error: e,
        title: 'Exportar',
        userMessage,
      });
    } finally {
      setLoading(false);
    }
  }, [mode, adapter, branchId]);

  const pickAndPreview = useCallback(async () => {
    let picked: Awaited<ReturnType<typeof pickCsvFile>> = null;
    try {
      picked = await pickCsvFile();
    } catch (e) {
      // pickCsvFile ya hizo presentUnexpectedFailure (toast + Sentry).
      if (!isUnexpectedFailureHandled(e)) {
        presentUnexpectedFailure({ module: 'shared', op: 'importar', error: e, title: 'Importar', userMessage: 'No se pudo leer el archivo.' });
      }
      return;
    }
    if (!picked) {
      return;
    }

    const lowerName = picked.name.toLowerCase();
    if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls') || picked.text.startsWith('PK')) {
      showInfo(
        'Importar',
        'Elegí un archivo CSV (en Excel: Guardar como → CSV). Los archivos .xlsx no se pueden importar.'
      );
      return;
    }

    try {
      setLoading(true);
      setFileName(picked.name);
      setCsvText(picked.text);

      const result = await adapter.previewImport(picked.text);
      setPreview(result);
      if (result.blocked) {
        showError('Importar', result.blockMessage ?? 'No se puede importar.');
      }
      setStep('preview');
    } catch (e) {
      presentUnexpectedFailure({
        module: 'csv',
        op: 'preview',
        error: e,
        title: 'Importar',
        userMessage: e instanceof Error ? e.message : 'No se pudo leer el archivo.',
      });
    } finally {
      setLoading(false);
    }
  }, [adapter]);

  const confirmImport = useCallback(async () => {
    if (!csvText) return;
    try {
      setCommitting(true);
      const result = await adapter.commitImport(csvText);
      const breakdown = countOkBreakdown(preview?.rows);
      setLastErrors(result.errors);
      setLastDoneCounts({
        applied: result.applied,
        skipped: result.skipped,
        failed: result.failed,
        altas: result.altas ?? 0,
        ediciones: result.ediciones ?? breakdown.ediciones,
      });

      // ADR-017 online: commitImport persiste en servidor; refresh opcional del read model.
      if (Platform.OS === 'web') {
        try {
          await adapter.syncSnapshot?.();
        } catch {
          // onApplied recarga lo que haya en caché RAM.
        }
      }

      if (onApplied) {
        await onApplied();
      }

      setStep('done');
    } catch (e) {
      presentUnexpectedFailure({
        module: 'csv',
        op: 'commit',
        error: e,
        title: 'Importar',
        userMessage: e instanceof Error ? e.message : 'No se pudo completar la importación.',
      });
    } finally {
      setCommitting(false);
    }
  }, [
    csvText,
    adapter,
    preview,
    countOkBreakdown,
    onApplied,
  ]);

  const canConfirmImport = useMemo(() => {
    if (!preview) return false;
    return preview.okCount > 0 && !preview.blocked;
  }, [preview]);

  const downloadErrorReport = useCallback(async () => {
    if (lastErrors.length === 0) return;
    try {
      const csv = adapter.buildImportErrorsCsv(lastErrors);
      await shareOrDownloadCsv(csv, 'errores_import.csv');
    } catch (e) {
      const userMessage = e instanceof Error ? e.message : 'No se pudo generar el archivo.';
      if (isUnexpectedFailureHandled(e)) {
        showError('Errores', userMessage);
        return;
      }
      presentUnexpectedFailure({
        module: 'csv',
        op: 'export_errors',
        error: e,
        title: 'Errores',
        userMessage,
      });
    }
  }, [lastErrors, adapter]);

  return {
    step,
    setStep,
    entityLabel,
    fileName,
    preview,
    canConfirmImport,
    loading,
    committing,
    lastDoneCounts,
    lastErrors,
    reset,
    downloadTemplate,
    downloadExport,
    pickAndPreview,
    confirmImport,
    downloadErrorReport,
  };
}

export type UseCsvImportExportModalParams = {
  visible: boolean;
  mode: CsvFlowMode;
  catalogEntity?: CatalogCsvEntityKind;
  branchId?: string | null;
  branchLabel?: string;
  maxProducts?: number | null;
  performedByUserId?: string | null;
  onApplied?: () => void | Promise<void>;
  adapter: CsvFlowAdapter;
};

export function useCsvImportExportModal({
  visible,
  mode,
  catalogEntity = 'products',
  branchId = null,
  branchLabel,
  maxProducts = null,
  performedByUserId = null,
  onApplied,
  adapter,
}: UseCsvImportExportModalParams) {
  const [columnsExpanded, setColumnsExpanded] = useState(false);
  const [previewErrorsOnly, setPreviewErrorsOnly] = useState(false);
  const [previewWarningsOnly, setPreviewWarningsOnly] = useState(false);
  const [showEqualRows, setShowEqualRows] = useState(false);
  const [previewPageIndex, setPreviewPageIndex] = useState(0);
  const [previewPageSize, setPreviewPageSize] = useState<PreviewPageSize>(10);

  const flow = useCsvImportExportFlow({
    mode,
    catalogEntity,
    branchId,
    branchLabel,
    maxProducts,
    performedByUserId,
    onApplied,
    adapter,
  });

  useEffect(() => {
    if (!visible) {
      flow.reset();
      setColumnsExpanded(false);
      setPreviewErrorsOnly(false);
      setPreviewWarningsOnly(false);
      setShowEqualRows(false);
      setPreviewPageIndex(0);
      setPreviewPageSize(10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const allPreviewRows = useMemo(() => {
    if (flow.preview) {
      return flow.preview.rows.map((r) => ({
        rowNumber: r.rowNumber,
        status: r.status,
        action: r.action,
        detail: r.detail,
        message: r.message,
      }));
    }
    return [];
  }, [flow.preview]);

  const previewCounts = useMemo<PreviewCounts>(() => {
    let altas = 0;
    let modificaciones = 0;
    let errores = 0;
    let avisos = 0;
    let iguales = 0;
    for (const r of allPreviewRows) {
      if (r.status === 'error') errores += 1;
      else if (r.status === 'skip') iguales += 1;
      else if (r.action === 'crear') altas += 1;
      else modificaciones += 1;
      if (previewRowHasAviso(r)) avisos += 1;
    }
    return { altas, modificaciones, errores, avisos, iguales };
  }, [allPreviewRows]);

  const previewRows = useMemo(() => {
    let filtered = allPreviewRows;
    if (previewErrorsOnly) {
      filtered = allPreviewRows.filter((r) => r.status === 'error');
    } else if (previewWarningsOnly) {
      filtered = allPreviewRows.filter((r) => previewRowHasAviso(r));
    } else if (!showEqualRows) {
      filtered = allPreviewRows.filter((r) => r.status !== 'skip');
    }
    return sortPreviewRowsForReview(filtered);
  }, [allPreviewRows, previewErrorsOnly, previewWarningsOnly, showEqualRows]);

  useEffect(() => {
    setPreviewPageIndex(0);
  }, [allPreviewRows, previewErrorsOnly, previewWarningsOnly, showEqualRows, flow.step]);

  const previewTotalCount = previewRows.length;
  const maxPreviewPageIndex = Math.max(0, Math.ceil(previewTotalCount / previewPageSize) - 1);
  useEffect(() => {
    if (previewPageIndex > maxPreviewPageIndex) {
      setPreviewPageIndex(maxPreviewPageIndex);
    }
  }, [previewPageIndex, maxPreviewPageIndex]);

  const pagedPreviewRows = useMemo(() => {
    const start = previewPageIndex * previewPageSize;
    return previewRows.slice(start, start + previewPageSize);
  }, [previewRows, previewPageIndex, previewPageSize]);

  const columnHints = useMemo(() => getCsvColumnHints(mode, catalogEntity), [mode, catalogEntity]);
  const guideNote = useMemo(() => getCsvGuideNote(mode, catalogEntity), [mode, catalogEntity]);
  const heroSubtitle = useMemo(() => getCsvHeroSubtitle(mode, catalogEntity), [mode, catalogEntity]);

  return {
    flow,
    columnsExpanded,
    setColumnsExpanded,
    previewErrorsOnly,
    setPreviewErrorsOnly,
    previewWarningsOnly,
    setPreviewWarningsOnly,
    showEqualRows,
    setShowEqualRows,
    previewPageIndex,
    setPreviewPageIndex,
    previewPageSize,
    setPreviewPageSize,
    allPreviewRows,
    previewCounts,
    previewRows,
    previewTotalCount,
    pagedPreviewRows,
    columnHints,
    guideNote,
    heroSubtitle,
  };
}
